class ProductGallery {
  constructor() {
    this.container = document.querySelector(".product-gallery");
    if (!this.container) return;

    this.mainWrapper = this.container.querySelector(
      ".product-gallery__main-wrapper"
    );
    this.mediaItems = this.container.querySelectorAll(".product__media-item");
    this.thumbnails = this.container.querySelectorAll(".thumbnail-button");
    this.paginationDots = this.container.querySelectorAll(".pagination-dot");
    this.currentIndex = 0;
    this.isDragging = false;
    this.startPos = 0;
    this.startYPos = null;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.isRTL =
      document.documentElement.dir === "rtl" || window.is_rtl === true;

    this.init();
    this.initPhotoSwipe();
  }

  getSlideOffset(index) {
    return this.isRTL ? index * 100 : index * -100;
  }

  init() {
    // Main gallery navigation
    this.container.querySelectorAll(".gallery-nav").forEach((button) => {
      button.addEventListener("click", () => {
        const direction = button.classList.contains("next") ? 1 : -1;
        const step = this.isRTL ? -direction : direction;
        this.moveToIndex(this.currentIndex + step);
      });
    });

    // Thumbnail clicks
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener("click", () => this.moveToIndex(index));
    });

    // Pagination dots clicks
    this.paginationDots.forEach((dot, index) => {
      dot.addEventListener("click", () => this.moveToIndex(index));
    });

    // Touch events for main gallery
    this.mainWrapper.addEventListener(
      "touchstart",
      this.touchStart.bind(this),
      { passive: false }
    );
    this.mainWrapper.addEventListener("touchmove", this.touchMove.bind(this), {
      passive: false,
    });
    this.mainWrapper.addEventListener("touchend", this.touchEnd.bind(this));

    // Mouse events for main gallery
    if (window.innerWidth > 768) {
      this.mainWrapper.addEventListener(
        "mousedown",
        this.touchStart.bind(this)
      );
      this.mainWrapper.addEventListener("mousemove", this.touchMove.bind(this));
      this.mainWrapper.addEventListener("mouseup", this.touchEnd.bind(this));
      this.mainWrapper.addEventListener("mouseleave", this.touchEnd.bind(this));
    }

    // Thumbnail scroll navigation
    const thumbsContainer = this.container.querySelector(
      ".product-gallery__thumbnails"
    );
    const thumbNavs = this.container.querySelectorAll(".thumbnail-nav");
    const prevNav = this.container.querySelector(".thumbnail-nav.prev");
    const nextNav = this.container.querySelector(".thumbnail-nav.next");

    // Update button states
    const updateNavStates = () => {
      if (!thumbsContainer) return;
      const atTop = thumbsContainer.scrollTop <= 1;
      const atBottom = thumbsContainer.scrollTop + thumbsContainer.clientHeight >= thumbsContainer.scrollHeight - 1;
      
      prevNav?.classList.toggle("disabled", atTop);
      nextNav?.classList.toggle("disabled", atBottom);
    };

    thumbNavs.forEach((nav) => {
      nav.addEventListener("click", () => {
        const direction = nav.classList.contains("next") ? 1 : -1;
        thumbsContainer.scrollBy({ top: direction * 100, behavior: "smooth" });
      });
    });

    thumbsContainer.addEventListener("scroll", updateNavStates);

    // Show/hide thumbnail navigation buttons
    const observerCallback = (entries) => {
      const thumbsWrapper = this.container.querySelector(
        ".product-gallery__thumbnails"
      );
      const showButtons = thumbsWrapper.scrollHeight > thumbsWrapper.clientHeight;
      thumbNavs.forEach(
        (nav) => (nav.style.display = showButtons ? "block" : "none")
      );
      updateNavStates();
    };

    const observer = new ResizeObserver(observerCallback);
    observer.observe(thumbsContainer);

    updateNavStates();
  }

  moveToIndex(index) {
    const totalItems = this.mediaItems.length;
    this.currentIndex = Math.max(0, Math.min(index, totalItems - 1));

    // Update main gallery
    const offset = this.getSlideOffset(this.currentIndex);
    this.prevTranslate = offset;
    this.currentTranslate = offset;
    this.mediaItems.forEach((item) => {
      item.style.transform = `translateX(${offset}%)`;
    });

    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle("is-active", i === this.currentIndex);
      thumb.setAttribute("aria-current", i === this.currentIndex);
    });

    // Update pagination dots
    this.paginationDots.forEach((dot, i) => {
      dot.classList.toggle("is-active", i === this.currentIndex);
      dot.setAttribute("aria-current", i === this.currentIndex);
    });

    // Ensure active thumbnail is visible
    const activeThumb = this.thumbnails[this.currentIndex];
    activeThumb.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }

  // Add setActiveMedia method for compatibility with standard media-gallery
  setActiveMedia(mediaId, prepend = false) {
    const targetIndex = Array.from(this.mediaItems).findIndex(item => 
      item.getAttribute('data-media-id') === mediaId
    );
    
    if (targetIndex !== -1) {
      this.moveToIndex(targetIndex);
    }
  }

  touchStart(event) {
    this.isDragging = true;
    this.startPos = event.type.includes("mouse")
      ? event.pageX
      : event.touches[0].clientX;
    this.currentTranslate = this.prevTranslate;
  }

  touchMove(event) {
    if (!this.isDragging) return;

    const currentPosition = event.type.includes("mouse")
      ? event.pageX
      : event.touches[0].clientX;

    // Get the Y position to detect vertical scrolling
    const currentYPosition = event.type.includes("mouse")
      ? event.pageY
      : event.touches[0].clientY;

    const diff = currentPosition - this.startPos;
    const move = Math.abs(diff);

    // Check if this is primarily a horizontal swipe
    // Get the initial Y position if we haven't stored it yet
    if (!this.startYPos) {
      this.startYPos = event.type.includes("mouse")
        ? event.pageY
        : event.touches[0].clientY;
    }

    // Calculate vertical movement
    const verticalDiff = Math.abs(currentYPosition - this.startYPos);
    
    // Only prevent default and handle as swipe if horizontal movement is greater than vertical
    // and greater than our minimum threshold
    if (move > 5 && move > verticalDiff) {
      // This is a horizontal swipe, prevent default scrolling
      event.preventDefault(); // Prevent page scrolling while swiping
      const containerWidth = this.mainWrapper.offsetWidth;
      const dragDelta = (diff / containerWidth) * 100;
      this.currentTranslate = this.isRTL
        ? this.prevTranslate - dragDelta
        : this.prevTranslate + dragDelta;

      // Apply the transform immediately for smooth dragging
      this.mediaItems.forEach((item) => {
        item.style.transform = `translateX(${this.currentTranslate}%)`;
      });
    }
  }

  touchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    // Reset the Y position tracking
    this.startYPos = null;

    const movedBy = this.currentTranslate - this.prevTranslate;

    if (Math.abs(movedBy) > 20) {
      // 20% threshold for swipe
      const movedForward = this.isRTL ? movedBy > 0 : movedBy < 0;
      if (movedForward) {
        this.moveToIndex(this.currentIndex + 1);
      } else {
        this.moveToIndex(this.currentIndex - 1);
      }
    } else {
      this.moveToIndex(this.currentIndex); // Snap back to current
    }
  }

  initPhotoSwipe() {
    const zoomButtons = document.querySelectorAll(".zoom-button");
    let currentGallery = null;

    zoomButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();

        // Destroy existing gallery if it exists
        if (currentGallery) {
          currentGallery.close();
          currentGallery = null;
        }

        // Safety check for required elements
        if (!this.mediaItems || this.mediaItems.length === 0) {
          console.warn('No media items found');
          return;
        }

        // Enhanced items mapping to support both images and videos
        const items = Array.from(this.mediaItems)
          .map((item) => {
            const mediaType = item.getAttribute('data-media-type');
            const canZoom = item.getAttribute('data-can-zoom') === 'true'; // 从源头读取缩放标识
            const img = item.querySelector("img:not(.discount-flag-img)"); // Exclude discount flags
            
            if (!img) return null;

            // Base item structure
            const baseItem = {
              msrc: img.src, // Thumbnail for loading
              canZoom: canZoom, // 添加缩放标识到item
            };

            // Handle different media types
            if (mediaType === 'image') {
              return {
                ...baseItem,
                src: img.src.replace(/(_[0-9]+x[0-9]+)?\.jpg/, "_2048x2048.jpg"),
                w: 2048,
                h: 2048,
              };
            } else if (mediaType === 'external_video') {
              const videoHost = item.getAttribute('data-video-host');
              const videoId = item.getAttribute('data-video-id');
              return {
                ...baseItem,
                html: this.getVideoHTML(videoHost, videoId),
                mediaType: 'video',
                videoHost: videoHost,
                videoId: videoId,
              };
            } else if (mediaType === 'video') {
              const videoUrl = item.getAttribute('data-video-url');
              const videoType = item.getAttribute('data-video-type') || 'video/mp4';
              
              // Debug log
              console.log('Video URL:', videoUrl);
              console.log('Video Type:', videoType);
              
              return {
                ...baseItem,
                html: this.getHostedVideoHTML(videoUrl, videoType),
                mediaType: 'video',
                videoUrl: videoUrl,
                videoType: videoType,
              };
            }
            
            return null;
          })
          .filter((item) => item !== null); // Remove any null items

        // Check if we have valid items
        if (!items || items.length === 0) {
          console.warn('No valid media items to display');
          return;
        }

        const options = {
          index: Array.from(this.mediaItems).indexOf(
            button.closest(".product__media-item")
          ),
          
          // 背景和动画
          bgOpacity: 0.9,
          showHideOpacity: true,
          
          // 历史记录
          history: false,
          
          // UI元素 - 使用PhotoSwipe原生API
          counterEl: true,        // 显示计数器 (1/5)
          arrowEl: true,          // 显示左右箭头
          fullscreenEl: true,     // 显示全屏按钮
          zoomEl: true,           // 显示缩放按钮
          preloaderEl: true,      // 显示预加载器
          
          // 交互行为 - PhotoSwipe原生API
          closeOnScroll: false,           // 禁止滚动时关闭
          closeOnVerticalDrag: true,      // 允许垂直拖动关闭
          tapToClose: false,              // 禁止点击背景关闭（避免误操作）
          tapToToggleControls: true,      // 点击切换控制栏
          pinchToClose: true,             // 捏合手势关闭
          loop: true,                     // 循环浏览
          escKey: true,                   // ESC键关闭
          arrowKeys: true,                // 箭头键导航
          
          // 分享功能
          shareEl: false,
          shareButtons: [
            {
              id: "facebook",
              label: "Share on Facebook",
              url: "https://www.facebook.com/sharer/sharer.php?u={{url}}",
            },
            {
              id: "twitter",
              label: "Tweet",
              url: "https://twitter.com/intent/tweet?text={{text}}&url={{url}}",
            },
            {
              id: "pinterest",
              label: "Pin it",
              url: "http://www.pinterest.com/pin/create/button/?url={{url}}&media={{image_url}}&description={{text}}",
            },
          ],
          getImageURLForShare: function (shareButtonData) {
            return gallery.currItem.src || "";
          },
          getPageURLForShare: function (shareButtonData) {
            return window.location.href;
          },
          getTextForShare: function (shareButtonData) {
            return document.title;
          },

          // Add these options for thumbnails
          showAnimationDuration: 0,
          hideAnimationDuration: 0,
          galleryPIDs: true,
          galleryUID: "1",
          getThumbBoundsFn: (index) => {
            // Safety checks for thumbnails
            if (!this.thumbnails || this.thumbnails.length === 0) {
              return { x: 0, y: 0, w: 0 };
            }
            
            const thumbnail = this.thumbnails[index];
            if (!thumbnail) {
              return { x: 0, y: 0, w: 0 };
            }
            
            const img = thumbnail.querySelector("img");
            if (!img) {
              return { x: 0, y: 0, w: 0 };
            }
            
            const pageYScroll =
              window.pageYOffset || document.documentElement.scrollTop;
            const rect = img.getBoundingClientRect();
            return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
          },
        };

        currentGallery = new PhotoSwipe(
          document.querySelector(".pswp"),
          PhotoSwipeUI_Default,
          items,
          options
        );

        // Clear existing thumbnails before creating new ones
        currentGallery.listen("initialZoomInEnd", function () {
          const existingThumbs = document.querySelector(".pswp__thumbnails");
          if (existingThumbs) {
            existingThumbs.remove();
          }

          const container = document.createElement("div");
          container.className = "pswp__thumbnails";
          
          // Create inner flex wrapper
          const innerFlex = document.createElement("div");
          innerFlex.className = "pswp__thumbnails-in-flex";
          
          items.forEach((item, index) => {
            // Skip if item or msrc is missing
            if (!item || !item.msrc) {
              return;
            }
            
            const thumb = document.createElement("div");
            thumb.className = "pswp__thumbnail";
            
            // Add video indicator class if it's a video
            if (item.mediaType === 'video') {
              thumb.classList.add('pswp__thumbnail--video');
            }
            
            if (index === currentGallery.getCurrentIndex()) {
              thumb.classList.add("active");
            }
            thumb.innerHTML = `<img src="${item.msrc}" alt="Thumbnail ${
              index + 1
            }">`;
            thumb.addEventListener("click", () => {
              currentGallery.goTo(index);
            });
            innerFlex.appendChild(thumb);
          });
          
          // Append inner flex to container
          container.appendChild(innerFlex);
          
          // Only append if we have thumbnails
          if (innerFlex.children.length > 0 && currentGallery.scrollWrap) {
            currentGallery.scrollWrap.appendChild(container);
            
            // Scroll to active thumbnail on init
            setTimeout(() => {
              const activeThumb = container.querySelector('.pswp__thumbnail.active');
              if (activeThumb) {
                const containerWidth = container.offsetWidth;
                const thumbLeft = activeThumb.offsetLeft;
                const thumbWidth = activeThumb.offsetWidth;
                const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);
                
                container.scrollTo({
                  left: scrollLeft,
                  behavior: 'smooth'
                });
              }
            }, 100);
          }

          // Initial zoom control check - 使用源头标识
          const currentIndex = currentGallery.getCurrentIndex();
          const currentItem = items[currentIndex];
          const zoomButton = document.querySelector('.pswp__button--zoom');
          
          // 直接读取canZoom标识，无需复杂判断
          if (zoomButton && currentItem) {
            if (!currentItem.canZoom) {
              // 禁用所有缩放功能
              zoomButton.style.display = 'none';
              zoomButton.style.visibility = 'hidden';
              currentGallery.options.pinchToClose = false;
              currentGallery.options.maxSpreadZoom = 1; // 禁止放大
              currentGallery.options.getDoubleTapZoom = function() {
                return 1; // 双击不放大
              };
              
              // 设置初始缩放级别
              if (currentGallery.currItem) {
                currentGallery.currItem.initialZoomLevel = 1;
                currentGallery.currItem.fitRatio = 1;
              }
            }
          }
        });

        // Update thumbnail active states when image changes
        currentGallery.listen("beforeChange", function () {
          const currentIndex = currentGallery.getCurrentIndex();
          const thumbs = document.querySelectorAll(".pswp__thumbnail");
          const thumbsContainer = document.querySelector(".pswp__thumbnails");
          
          thumbs.forEach((thumb, i) => {
            thumb.classList.toggle("active", i === currentIndex);
          });

          // Scroll active thumbnail into view
          if (thumbs[currentIndex] && thumbsContainer) {
            const activeThumb = thumbs[currentIndex];
            const containerWidth = thumbsContainer.offsetWidth;
            const thumbLeft = activeThumb.offsetLeft;
            const thumbWidth = activeThumb.offsetWidth;
            const scrollLeft = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);
            
            thumbsContainer.scrollTo({
              left: scrollLeft,
              behavior: 'smooth'
            });
          }

          // Stop videos when changing slides
          const videos = document.querySelectorAll('.pswp__video-wrapper video');
          videos.forEach(video => {
            video.pause();
          });

          // For iframes (YouTube/Vimeo), we need to reload them to stop
          const iframes = document.querySelectorAll('.pswp__video-wrapper iframe');
          iframes.forEach(iframe => {
            const src = iframe.src;
            iframe.src = '';
            // Only reload if we're navigating away from a video
            if (!items[currentIndex].html) {
              iframe.src = src;
            }
          });
        });

        // Control zoom functionality - 使用源头标识
        currentGallery.listen("afterChange", function () {
          const currentIndex = currentGallery.getCurrentIndex();
          const currentItem = items[currentIndex];
          const zoomButton = document.querySelector('.pswp__button--zoom');
          
          if (zoomButton && currentItem) {
            // 直接使用canZoom标识，简单明确
            if (currentItem.canZoom) {
              // 可缩放：显示缩放按钮和启用缩放功能
              zoomButton.style.display = '';
              zoomButton.style.visibility = '';
              currentGallery.options.pinchToClose = true;
              currentGallery.options.maxSpreadZoom = 2; // 允许放大到2倍
              currentGallery.options.getDoubleTapZoom = function(isMouseClick, item) {
                return item.initialZoomLevel < 1 ? 1 : 1.5;
              };
            } else {
              // 不可缩放：隐藏缩放按钮和禁用所有缩放功能
              zoomButton.style.display = 'none';
              zoomButton.style.visibility = 'hidden';
              currentGallery.options.pinchToClose = false;
              currentGallery.options.maxSpreadZoom = 1; // 禁止放大
              currentGallery.options.getDoubleTapZoom = function() {
                return 1; // 双击也不放大
              };
            }
          }
          
          // 强制更新当前item的缩放级别
          if (currentGallery.currItem && !currentItem.canZoom) {
            currentGallery.currItem.initialZoomLevel = 1;
            currentGallery.currItem.fitRatio = 1;
            if (currentGallery.currItem.container) {
              currentGallery.zoomTo(1, {x:0, y:0}, 0);
            }
          }
        });

        // Clean up on close
        currentGallery.listen("destroy", function () {
          const thumbnailContainer =
            document.querySelector(".pswp__thumbnails");
          if (thumbnailContainer) {
            thumbnailContainer.remove();
          }
          
          // Stop all videos on close
          const videos = document.querySelectorAll('.pswp__video-wrapper video');
          videos.forEach(video => {
            video.pause();
            video.src = '';
          });
          
          currentGallery = null;
        });

        // Handle custom HTML content (videos)
        currentGallery.listen('gettingData', function(index, item) {
          if (item.html) {
            item.w = 1200;
            item.h = 675; // 16:9 aspect ratio
          }
        });

        // Alert when PhotoSwipe opens
        currentGallery.listen('afterInit', function() {
          document.body.classList.add('photo-swipe-open');
        });

        // Alert when PhotoSwipe closes
        currentGallery.listen('close', function() {
          setTimeout(() => {
            document.body.classList.remove('photo-swipe-open');
          }, 100);
        });

        currentGallery.init();
      });
    });
  }

  // Generate YouTube/Vimeo embed HTML
  getVideoHTML(host, videoId) {
    if (host === 'youtube') {
      return `
        <div class="pswp__video-container">
          <div class="pswp__video-wrapper">
            <iframe 
              src="https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0" 
              frameborder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>
        </div>
      `;
    } else if (host === 'vimeo') {
      return `
        <div class="pswp__video-container">
          <div class="pswp__video-wrapper">
            <iframe 
              src="https://player.vimeo.com/video/${videoId}?autoplay=1" 
              frameborder="0" 
              allow="autoplay; fullscreen; picture-in-picture" 
              allowfullscreen>
            </iframe>
          </div>
        </div>
      `;
    }
    return '';
  }

  // Generate hosted video HTML
  getHostedVideoHTML(videoUrl, videoType = 'video/mp4') {
    // Check if videoUrl is valid
    if (!videoUrl) {
      console.warn('No video URL provided');
      return '<div class="pswp__video-container"><p>Video not available</p></div>';
    }
    
    return `
      <div class="pswp__video-container">
        <div class="pswp__video-wrapper">
          <video controls autoplay playsinline>
            <source src="${videoUrl}" type="${videoType}">
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    `;
  }
}

// Initialize the gallery when the DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  window.productGalleryInstance = new ProductGallery();
});

// Add zoom functionality
function initImageZoom() {
  // Add check for screen size
  if (window.innerWidth <= 768) return; // Exit if screen is too small

  const zoomResult = document.getElementById("global-zoom-result");
  const zoomWrappers = document.querySelectorAll(".image-zoom-wrapper");

  function setupZoom(wrapper) {
    // Add check for screen size
    if (window.innerWidth <= 768) return; // Exit if screen is too small

    const img = wrapper.querySelector("img");
    const lens = wrapper.querySelector(".image-zoom-lens");

    function moveLens(e) {
      let pos = getCursorPos(e);
      let x = pos.x - lens.offsetWidth / 2;
      let y = pos.y - lens.offsetHeight / 2;

      // Prevent lens from going outside the image
      if (x > wrapper.offsetWidth - lens.offsetWidth) {
        x = wrapper.offsetWidth - lens.offsetWidth;
      }
      if (x < 0) {
        x = 0;
      }
      if (y > wrapper.offsetHeight - lens.offsetHeight) {
        y = wrapper.offsetHeight - lens.offsetHeight;
      }
      if (y < 0) {
        y = 0;
      }

      // Set lens position
      lens.style.left = x + "px";
      lens.style.top = y + "px";

      // Calculate and set background position for result
      let cx = zoomResult.offsetWidth / lens.offsetWidth;
      let cy = zoomResult.offsetHeight / lens.offsetHeight;

      zoomResult.style.backgroundImage = "url('" + img.src + "')";
      zoomResult.style.backgroundSize =
        wrapper.offsetWidth * cx + "px " + wrapper.offsetHeight * cy + "px";
      zoomResult.style.backgroundPosition =
        "-" + x * cx + "px -" + y * cy + "px";
    }

    function getCursorPos(e) {
      let rect = wrapper.getBoundingClientRect();
      let x = e.pageX - rect.left - window.scrollX;
      let y = e.pageY - rect.top - window.scrollY;
      return { x, y };
    }

    // Mouse events
    wrapper.addEventListener("mouseenter", () => {
      document.body.classList.add("zoom-active");
    });

    wrapper.addEventListener("mouseleave", () => {
      document.body.classList.remove("zoom-active");
    });

    wrapper.addEventListener("mousemove", moveLens);
  }

  // Initialize zoom for all wrappers
  zoomWrappers.forEach(setupZoom);

  // Handle thumbnail clicks
  const thumbnailButtons = document.querySelectorAll(".thumbnail-button");
  thumbnailButtons.forEach((button) => {
    button.addEventListener("click", () => {
      setTimeout(() => {
        const activeWrapper = document.querySelector(
          ".product__media-item.is-active .image-zoom-wrapper"
        );
        if (activeWrapper) {
          // Remove old event listeners
          const oldWrapper = document.querySelector(".zoom-initialized");
          if (oldWrapper) {
            oldWrapper.classList.remove("zoom-initialized");
          }
          // Setup new zoom
          activeWrapper.classList.add("zoom-initialized");
          setupZoom(activeWrapper);
        }
      }, 300);
    });
  });

  // Add window resize handler
  window.addEventListener("resize", () => {
    if (window.innerWidth <= 768) {
      document.body.classList.remove("zoom-active");
    }
  });
}

// Initialize zoom functionality
document.addEventListener("DOMContentLoaded", initImageZoom);
